import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import trackError from '@helpers/trackError'
import { DEV_FLAG_OVERRIDES } from '@lib/flags'

type ABTests = {
  tests: string[]
  testsVariants: Record<string, string>
  testsLoaded: boolean
}

const defaultState: ABTests = {
  tests: [],
  testsVariants: {},
  testsLoaded: false
}

const ABTestsContext = createContext<ABTests>(defaultState)

export function ABTestsProvider({ children }: { children: ReactNode }) {
  const [abTests, setABTests] = useState<ABTests>(defaultState)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setABTests({
        tests: Object.keys(DEV_FLAG_OVERRIDES),
        testsVariants: DEV_FLAG_OVERRIDES,
        testsLoaded: true
      })
      return
    }

    const awaitPostHog = async () => {
      await window.postHogAsyncLoader
      const posthog = window.posthog
      posthog?.onFeatureFlags((flags, flagVariants) => {
        setTimeout(() => {
          Object.entries(flagVariants).forEach(([flag, variant]) => {
            posthog.capture('$feature_flag_called', {
              $feature_flag: flag,
              $feature_flag_response: variant
            })
          })
        }, 0)
        setABTests({
          tests: flags,
          testsVariants: flagVariants,
          testsLoaded: true
        })
      })
    }

    awaitPostHog().catch((error) => {
      trackError(new Error('Failed to get PostHog feature flags'), {
        error: error as Error,
        section: 'ABTests'
      })
    })
  }, [])

  return (
    <ABTestsContext.Provider value={abTests}>
      {children}
    </ABTestsContext.Provider>
  )
}

export function useABTestsContext(): ABTests {
  return useContext(ABTestsContext)
}
